/* eslint-disable */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getSetting } from "../utils/settings";

const SECTIONS = [
  { key: "book_now", label: "Book Now" },
  { key: "book_far_ahead", label: "Book Ahead" },
  { key: "book_day_of", label: "Day Of" },
  { key: "booked", label: "Booked ✓" },
];

const sectionHeaderStyle = {
  fontSize: 10, letterSpacing: "0.14em",
  fontFamily: "monospace", color: "#8B7355",
  textTransform: "uppercase", marginBottom: 8, marginTop: 20,
};

export default function AvantLeDepart() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [dayMap, setDayMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch all days for day number + city lookup
        const daysSnap = await getDocs(
          query(collection(db, "days"), where("ownerUid", "==", auth.currentUser.uid), where("tripId", "==", tripId))
        );
        const dMap = {};
        daysSnap.docs.forEach(d => {
          const data = d.data();
          dMap[d.id] = { day: data.order || data.day || "?", city: data.city || "?" };
        });
        setDayMap(dMap);

        // COMPOSITE INDEX REQUIRED: activities — bookingStatus (in) + bookingLeadDays DESC
        // Actual index needed: bookingLeadDays Descending (Firestore "in" doesn't need its own index field)
        // Create at: Firebase Console > Firestore > Indexes > Add index
        // Fields: bookingLeadDays Descending, __name__ Descending, Query scope: Collection
        const q = query(
          collection(db, "activities"),
          where("ownerUid", "==", auth.currentUser.uid),
          where("tripId", "==", tripId),
          where("bookingStatus", "in", ["book_now", "book_far_ahead", "book_day_of", "booked"]),
          orderBy("bookingLeadDays", "desc")
        );
        const snap = await getDocs(q);
        setActivities(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("[AvantLeDepart] Fetch error:", e);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Apply avantScope filter: 'confirmed' shows only confirmed activities
  const avantScope = getSetting('wanderologie_settings.avantScope') || 'all';
  const filteredActivities = avantScope === 'confirmed'
    ? activities.filter(a => a.confirmed === true)
    : activities;

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#FAF7F2", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 14, opacity: 0.3, color: "#B07080" }}>✦</div>
          <div style={{ fontSize: 12, color: "#8B7355", fontFamily: "monospace", letterSpacing: "0.1em" }}>
            LOADING BOOKINGS…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#FAF7F2", fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* Header */}
      <div style={{
        padding: "0 18px",
        paddingTop: "max(18px, env(safe-area-inset-top))",
        paddingBottom: 16,
        borderBottom: "1px solid #E8DDD0",
        background: "#FFFFFF",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <button
          onClick={() => navigate(`/trip/${tripId}`)}
          style={{
            background: "#F0EAE0", border: "1px solid #E8DDD0",
            borderRadius: 10, color: "#8B7355", width: 44, height: 44,
            cursor: "pointer", fontSize: 20, display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          ‹
        </button>
        <div>
          <div style={{
            fontSize: 24, fontWeight: 400, color: "#2C1810",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            letterSpacing: "0.03em", lineHeight: 1.1,
          }}>
            Avant le Départ
          </div>
          <div style={{
            fontSize: 10, color: "#8B7355", fontFamily: "monospace",
            letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 3,
          }}>
            booking & trip checklist
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "0 18px 40px" }}>
        {filteredActivities.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#8B7355" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3, color: "#B07080" }}>✦</div>
            <div style={{ fontSize: 13, fontFamily: "monospace", letterSpacing: "0.08em", lineHeight: 1.6 }}>
              {avantScope === 'confirmed' && activities.length > 0
                ? "No confirmed activities yet"
                : <>No bookings to track yet.<br />Edit any activity to set its booking status.</>}
            </div>
            <div
              onClick={() => navigate(`/trip/${tripId}`)}
              style={{ fontSize: 13, color: "#7A3D50", cursor: "pointer", textDecoration: "none", marginTop: 12, fontFamily: "monospace" }}
            >
              ← Back to trip
            </div>
          </div>
        ) : (
          SECTIONS.map(section => {
            const items = filteredActivities.filter(a => a.bookingStatus === section.key);
            if (items.length === 0) return null;
            return (
              <div key={section.key}>
                <div style={sectionHeaderStyle}>{section.label}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {items.map(act => {
                    const dayInfo = dayMap[act.dayId];
                    return (
                      <div
                        key={act.id}
                        style={{
                          background: "#FFFFFF", border: "1px solid #E8DDD0",
                          borderRadius: 12, padding: "14px 16px",
                          display: "flex", alignItems: "center", gap: 12,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, color: "#2C1810", lineHeight: 1.3, marginBottom: 4 }}>
                            {act.title}
                          </div>
                          <div style={{ fontSize: 11, color: "#8B7355", fontFamily: "monospace" }}>
                            {dayInfo ? `Day ${dayInfo.day} · ${dayInfo.city}` : "—"}
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                            {act.bookingLeadDays != null && act.bookingStatus !== "booked" && (
                              <span style={{
                                background: "#FFF0F5", color: "#B07080",
                                fontSize: 11, fontFamily: "monospace",
                                borderRadius: 6, padding: "2px 8px",
                              }}>
                                Book {act.bookingLeadDays} days ahead
                              </span>
                            )}
                            {act.bookingStatus === "booked" && act.bookingConfirmationRef && (
                              <span style={{
                                color: "#4A7C59", fontSize: 12, fontFamily: "monospace",
                              }}>
                                {act.bookingConfirmationRef}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/trip/${act.tripId}`)}
                          style={{
                            background: "transparent", border: "none",
                            color: "#B07080", fontSize: 12, fontFamily: "monospace",
                            cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                            padding: "4px 0",
                          }}
                        >
                          → View
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
